import React, { memo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { getSuitabilityAllowedTestYn } from '@store/actions/customerAction';
import { showModal } from '@store/actions/commonAction';
import { getToday, addDates } from '@common/utils/date';
import { isEmptyString, isSuccessStatus, getUniqArray } from '@common/utils';
import SatDatePicker from '@components/common/satUI/SatDatePicker';
import SatModal from '@components/common/satUI/SatModal';
import { SuitabilityHeader } from '@container/customer';

/**
 * 적합성 진단일 등록 및 주요내용 확인 (step 1)
 */
const SuitabilityTestDate = ({
  setCurrentStep,
  setCompletedStepList,
  getSuitabilityKeys,
  setSuitabilityKeys,
  suitabilityKeys,
  suitabilityDate,
  setSuitabilityDate,
  isMinor,
  custNo,
  isUsd,
}) => {
  const [isOpenSuitabilityTestStartPopup, setIsOpenSuitabilityTestStartPopup] =
    useState(false); // 미성년자 진단시작 안내 문구 팝업
  const [isSuitabilityAllowedTest, setIsSuitabilityAllowedTest] =
    useState(false); // 당일 적합성 진단 진행 가능 여부
  const dispatch = useDispatch();

  const moveToNext = useCallback(status => {
    if (isSuccessStatus(status)) {
      setCompletedStepList(prev =>
        getUniqArray(prev.concat({ main: 1, sub: 1 })),
      );
      setCurrentStep({ main: 2, sub: 1 });
    }
  }, []);

  /**
   * 진단 시작
   */
  const handleTestStart = useCallback(suitabilityKeys => {
    if (
      [
        suitabilityKeys?.applicationNo ?? '',
        suitabilityKeys?.reportSerialNo ?? '',
      ].some(isEmptyString)
    ) {
      // 1. 발행번호, 진단지 시리얼 번호 채번
      getSuitabilityKeys({
        isUsd,
        paperYN: 'Y', // 서면 작성 시
        callback: ({ status, data }) => {
          if (isSuccessStatus(status)) {
            setSuitabilityKeys({
              applicationNo: data.applicationNo,
              reportSerialNo: data.reportSerialNo,
            });
            moveToNext(status);
          }
        },
      });
    }
  }, []);

  // 해당 진단일 적합성 진단 가능 여부
  const handleGetSuitabilityAllowedTestYn = useCallback((customerId, date) => {
    dispatch(
      getSuitabilityAllowedTestYn({
        customerId,
        testDate: date,
        isUsd, // isUsd 외화 적합성
        callback: ({ data }) => {
          setIsSuitabilityAllowedTest(
            data.allowedTestYN !== 'Y' ? false : true,
          );
          if (data.allowedTestYN !== 'Y') {
            dispatch(
              showModal({
                visible: true,
                msg: '당일 적합성 진단 결과가 있어 재진단 불가합니다. ',
              }),
            );
          }
        },
      }),
    );
  }, []);

  return (
    <>
      <SuitabilityHeader
        title={`1. ${isUsd ? '외화' : '변액'}보험 적합성 진단 - 주요 내용 확인`}
      />
      <div className="con-wrap bg-gray">
        <div className="app-wrap">
          <div className="app-box">
            <h2 className="tit-con mgt-0">적합성 진단일</h2>
            <p className="text-con mgb-8">적합성 진단일을 입력해 주세요.</p>
            <SatDatePicker
              value={suitabilityDate}
              size={'small'}
              disabledDates={[
                addDates(getToday(), 'year', -1, 'yyyyMMdd'),
                getToday('yyyyMMdd'),
              ]}
              onChange={value => {
                setSuitabilityDate(value);
                handleGetSuitabilityAllowedTestYn(custNo, value);
              }}
            />
            <p className="text-info ico-type">
              면담 또는 구두로 적합성 정보를 취득한 일자를 입력해 주세요
            </p>
          </div>
          <div className="app-box">
            <h2 className="tit-con mgt-0">
              {`${isUsd ? '외화' : '변액'}보험 적합성 진단 주요 내용 확인`}
            </h2>
            {isUsd ? (
              <>
                <p className="text-con">
                  본 확인서는 귀하의 연령, 재산상황, 보험가입의 목적 등을
                  파악하여 보험 계약자에게 가장 적합한 보험계약 체결을 권유하기
                  위한 기초자료로 활용됩니다.
                </p>
                <p className="text-con mgt-16">
                  허위 또는 불성실한 답변을 하시는 경우 부적합한 보험계약을
                  권유하게 될 수 있으며, 이로 인해 발생하는 모든 손해는
                  보험계약자 본인에게 귀속되오니 유념하여 주시기 바랍니다.
                </p>
                <p className="text-con mgt-16">
                  보험설계사 등 타인이 본 확인서를 대신 작성하거나 귀하에게
                  사실과 다른 내용의 답변을 기재하도록 유도하는 것은 불완전
                  판매에 해당합니다. 완전판매모니터링(해피콜) 과정에서 불완전
                  판매로 확인되는 경우, 보험가입이 거절될 수 있습니다.
                </p>
              </>
            ) : (
              <>
                <p className="text-con">
                  본 확인서는 「 금융소비자 보호에 대한 법률」
                  제17조(적합성원칙)에 따라 귀하의 연령,재산상황,보험가입의 목적
                  등을 파악하여 보험계약자에게 가장 적합한 보험계약 체결을
                  권유하기 위한 기초자료로 활용됩니다.
                </p>
                <p className="text-con mgt-16">
                  허위 또는 불성실한 답변을 하시는 경우 부적합한 보험계약을
                  권유하게 될 수 있으며, 이로 인해 발생하는 모든 손해는
                  보험계약자 본인에게 귀속되오니 유념하여 주시기 바랍니다. 또한
                  귀하가 기대하는 특별계정(펀드)의 수익률이 높을수록 손실위험도
                  높아질 수 있으므로 유의하여 주시기 바랍니다.
                </p>
                <p className="text-con mgt-16">
                  담당 모집자 등 타인이 본 확인서를 대신 작성하거나 귀하에게
                  사실과 다른 내용의 답변을 기재하도록 유도하는 것은 불완전
                  판매에 해당합니다. 완전판매모니터링 과정에서 불완전 판매로
                  확인되는 경우, 보험가입이 거절될 수 있습니다.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="con-bottom box-shadow bg-gray">
        <div className="btn-area">
          <div className="box-btns-center">
            <button
              type="button"
              className={`btn-s ${isUsd ? 'yellow' : 'blue'} btn-wide w-328`}
              disabled={
                suitabilityDate && isSuitabilityAllowedTest ? false : true
              }
              onClick={() => {
                if (isMinor) setIsOpenSuitabilityTestStartPopup(true);
                else handleTestStart(suitabilityKeys);
              }}
            >
              {'확인 및 진단시작'}
            </button>
          </div>
        </div>
      </div>

      <SatModal
        visible={isOpenSuitabilityTestStartPopup}
        setVisible={() => {
          setIsOpenSuitabilityTestStartPopup(false);
        }}
        type="info"
        msg="미성년자 변액보험 적합성 진단시 친권자/후견인이 변액보험 적합성 진단을 실시 하여야 합니다."
        onOk={{
          handle: () => {
            setIsOpenSuitabilityTestStartPopup(false);
            handleTestStart(suitabilityKeys);
          },
        }}
      />
    </>
  );
};

SuitabilityTestDate.propTypes = {
  setCurrentStep: PropTypes.func.isRequired,
  setCompletedStepList: PropTypes.func.isRequired,
  getSuitabilityKeys: PropTypes.func.isRequired, // (action)
  setSuitabilityKeys: PropTypes.func.isRequired, // (component func)
  suitabilityKeys: PropTypes.shape({
    applicationNo: PropTypes.string.isRequired,
    reportSerialNo: PropTypes.string.isRequired,
  }),
  suitabilityDate: PropTypes.string, // 진단일
  setSuitabilityDate: PropTypes.func.isRequired, //진단일 변경
  isMinor: PropTypes.bool,
  isUsd: PropTypes.bool,
};
SuitabilityTestDate.defaultProps = {};

export default memo(SuitabilityTestDate);
